import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Hourglass } from "lucide-react";
import { theme, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { SheetCard, StatusTag, DimensionHeading } from "./Drawing";
import { fmtDateOnly } from "../lib/dateOnly";
import { secondaryBtn } from "../pages/resilience/shared";

// Certification countdown (docs/MANAGEMENT-SYSTEM.md "Certification countdown", backend
// lib/certificationCountdown.ts). From each certifiable standard's next audit — the date its certification
// body booked or, where none is booked, the target the organisation sets here — the readiness tasks
// back-planned from it, each with its do-by date and the slack left. "Add to calendar" puts one on the
// calendar as an ordinary task; a task is done when the records say so.

interface Task { key: string; kind: string; title: string; detail: string; dueAt: string; slackDays: number; state: "done" | "planned" | "todo"; href: string; onCalendar: boolean }
interface Countdown { frameworkId: string; label: string; targetDate: string; auditType: string; source: "booked" | "target"; bureauName: string | null; daysToAudit: number; tasks: Task[]; behind: number }
interface TargetRow { frameworkId: string; label: string; targetDate: string | null; auditType: string | null; booked: boolean }

const AUDIT_TYPES: Array<[string, string]> = [["initial", "Initial certification"], ["surveillance", "Surveillance"], ["recertification", "Recertification"]];
const typeLabel = (t: string) => AUDIT_TYPES.find(([k]) => k === t)?.[1].toLowerCase() ?? t;
const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "4px 10px", fontSize: 12 };

function slackText(t: Task): string {
  if (t.state === "done") return "Done";
  if (t.slackDays < 0) return `${-t.slackDays} ${t.slackDays === -1 ? "day" : "days"} late`;
  if (t.slackDays === 0) return "Due today";
  return `${t.slackDays} ${t.slackDays === 1 ? "day" : "days"} to spare`;
}

export function CertificationCountdown() {
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("controls:write");
  const [params] = useSearchParams();
  const focus = params.get("countdown");
  const focusRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<{ countdowns: Countdown[]; targets: TargetRow[] } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [date, setDate] = useState("");
  const [auditType, setAuditType] = useState("initial");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get<{ countdowns: Countdown[]; targets: TargetRow[] }>("/certification-countdown")); }
    catch { setData(null); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (data) focusRef.current?.scrollIntoView({ block: "center" }); }, [data, focus]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); await load(); return true; }
    catch (e) { setError(apiErrorMessage(e, "That did not work. Please try again.")); return false; }
    finally { setBusy(false); }
  }

  if (!data || data.targets.length === 0) return null;
  const unbooked = data.targets.filter((t) => !t.booked);

  return (
    <div style={{ marginBottom: 20 }}>
      <DimensionHeading as="h3" count={data.countdowns.length}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Hourglass size={15} /> Certification countdown</span>
      </DimensionHeading>
      <div style={{ ...text.hint, marginTop: -4, marginBottom: 10 }}>
        The readiness work back-planned from each standard's next audit. A booked audit date comes from your certification body; where none is booked, set a target below.
      </div>
      {error && <div role="alert" style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 8 }}>{error}</div>}

      <div style={{ display: "grid", gap: 10 }}>
        {data.countdowns.map((c) => (
          <div key={c.frameworkId} ref={c.frameworkId === focus ? focusRef : undefined}>
            <SheetCard
              featured={c.frameworkId === focus}
              label={`${c.label}: ${typeLabel(c.auditType)} audit in ${c.daysToAudit} ${c.daysToAudit === 1 ? "day" : "days"}`}
              reference={fmtDateOnly(`${c.targetDate}T00:00:00.000Z`)}
              actions={c.behind > 0 ? <StatusTag tone="gap">{c.behind} behind</StatusTag> : <StatusTag tone="met">On track</StatusTag>}
            >
              <div style={{ ...text.hint, marginBottom: 8 }}>
                {c.source === "booked" ? `Booked with ${c.bureauName ?? "your certification body"}.` : "Your own target; no audit is booked yet."}
              </div>
              {c.tasks.length === 0 ? <div style={text.hint}>Nothing in the records needs doing before this audit.</div> : c.tasks.map((t) => (
                <div key={t.key} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", padding: "6px 0", borderBottom: "1px solid var(--c-line)", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ ...text.body, fontWeight: 600 }}><Link to={t.href} style={{ color: theme.color.text }}>{t.title}</Link></div>
                    <div style={text.hint}>{t.detail} Do by {fmtDateOnly(`${t.dueAt}T00:00:00.000Z`)}.</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                    <StatusTag tone={t.state === "done" ? "met" : t.slackDays < 0 ? "gap" : t.state === "planned" ? "partial" : "none"}>{t.state === "planned" && t.slackDays >= 0 ? "Planned" : slackText(t)}</StatusTag>
                    {canEdit && t.state !== "done" && (t.onCalendar
                      ? <span style={text.hint}>On the calendar</span>
                      : <button type="button" disabled={busy} style={smallBtn} onClick={() => void run(() => api.post(`/certification-countdown/${encodeURIComponent(c.frameworkId)}/tasks/${encodeURIComponent(t.key)}/accept`, {}))}>Add to calendar</button>)}
                  </div>
                </div>
              ))}
            </SheetCard>
          </div>
        ))}
      </div>

      {canEdit && unbooked.length > 0 && (
        <details style={{ marginTop: 10 }} open={data.countdowns.length === 0}>
          <summary style={{ ...text.meta, cursor: "pointer" }}>Targets for standards with no audit booked ({unbooked.length})</summary>
          <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
            {unbooked.map((t) => (
              <div key={t.frameworkId} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ ...text.body, minWidth: 220 }}>{t.label}</span>
                {editing === t.frameworkId ? (
                  <>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...inputStyle, width: 170 }} aria-label={`Target audit date for ${t.label}`} />
                    <select value={auditType} onChange={(e) => setAuditType(e.target.value)} style={{ ...inputStyle, width: 200 }} aria-label="Audit type">
                      {AUDIT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <button type="button" disabled={busy || !date} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} onClick={async () => { if (await run(() => api.put(`/certification-countdown/targets/${encodeURIComponent(t.frameworkId)}`, { targetDate: date, auditType }))) setEditing(null); }}>Save</button>
                    <button type="button" style={smallBtn} onClick={() => setEditing(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={text.hint}>{t.targetDate ? `${typeLabel(t.auditType ?? "initial")} audit aimed at ${fmtDateOnly(`${t.targetDate}T00:00:00.000Z`)}` : "No target"}</span>
                    <button type="button" style={smallBtn} onClick={() => { setEditing(t.frameworkId); setDate(t.targetDate ?? ""); setAuditType(t.auditType ?? "initial"); }}>{t.targetDate ? "Change" : "Set a target"}</button>
                    {t.targetDate && <button type="button" disabled={busy} style={smallBtn} onClick={() => void run(() => api.put(`/certification-countdown/targets/${encodeURIComponent(t.frameworkId)}`, { targetDate: null }))}>Clear</button>}
                  </>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
